import { Client } from '@connectrpc/connect';

import { AuthenticatorService } from './espb/authenticator_service_pb';
import { AuthenticatorType } from './espb/authenticator_type_pb';
import { knownEnumOrDefault, requireFieldNotEmpty } from './grpcUtils';
import { toSyncDto } from './sync';
import {
  AuthenticatorDto,
  AuthenticatorId,
  AuthenticatorListDto,
  AuthenticatorBlockDto,
  SyncUpdate,
  AuthenticatorCreationDto,
  RegeneratedProfileDto,
  toPbProfileRegenerateData,
} from './types';

export default class AuthenticatorServiceClient {
  constructor(private readonly espbClient: Client<typeof AuthenticatorService>) {}

  async modify(
    createAuthenticatorOps: AuthenticatorCreationDto[],
    deleteAuthenticatorIds: AuthenticatorId[],
    authenticatorBlock: AuthenticatorBlockDto,
    regeneratedProfileDtos: Array<RegeneratedProfileDto> = [],
  ): Promise<{
    authenticatorIds: AuthenticatorId[];
    syncUpdate: SyncUpdate;
  }> {
    const resp = await this.espbClient.modify({
      createAuthenticatorOps,
      authenticatorBlock: authenticatorBlock.blob,
      authenticatorBlockSignature: authenticatorBlock.signature,
      deleteAuthenticatorIds,
      profiles: regeneratedProfileDtos.map(toPbProfileRegenerateData),
    });
    return {
      authenticatorIds: resp.authenticatorIds,
      syncUpdate: toSyncDto(requireFieldNotEmpty(resp.syncUpdate)),
    };
  }

  async list(latestAuthenticatorBlockHash?: Uint8Array): Promise<AuthenticatorListDto> {
    const resp = await this.espbClient.list({
      latestAuthenticatorBlockHash,
    });

    const authenticators: AuthenticatorDto[] = resp.authenticators.map((a) => {
      const data = requireFieldNotEmpty(a.data);
      return {
        id: requireFieldNotEmpty(a.id),
        authenticatorType: knownEnumOrDefault(data.authenticatorType, AuthenticatorType),
        storableProfileSeedEncPubKey: requireFieldNotEmpty(data.storableProfileSeedEncPubKey),
        storableProfileSeedEncPubKeySignature: requireFieldNotEmpty(
          data.storableProfileSeedEncPubKeySignature,
        ),
        highSecurityProfileSeedEncPubKey: requireFieldNotEmpty(
          data.highSecurityProfileSeedEncPubKey,
        ),
        highSecurityProfileSeedEncPubKeySignature: requireFieldNotEmpty(
          data.highSecurityProfileSeedEncPubKeySignature,
        ),
        highSecurityIdentitySigPubKey: requireFieldNotEmpty(data.highSecurityIdentitySigPubKey),
        highSecurityLoginSigPubKey: requireFieldNotEmpty(data.highSecurityLoginSigPubKey),
        secretInfo: data.secretInfo,
        secretSalt: data.secretSalt,
        storableSigPubKey: requireFieldNotEmpty(data.storableSigPubKey),
        storableSigPubKeySignature: requireFieldNotEmpty(data.storableSigPubKeySignature),
        webauthn:
          data.webauthn ?
            {
              webauthnId: requireFieldNotEmpty(data.webauthn.webauthnId),
              prfSalt: requireFieldNotEmpty(data.webauthn.prfSalt),
            }
          : undefined,
      };
    });

    const blocks: AuthenticatorBlockDto[] = resp.authenticatorBlocks.map((as) => ({
      blob: requireFieldNotEmpty(as.blob),
      signature: requireFieldNotEmpty(as.signature),
    }));

    return {
      blocks,
      authenticators,
    };
  }

  async createInitialAuthenticatorBlock(blockDto: AuthenticatorBlockDto): Promise<SyncUpdate> {
    const resp = await this.espbClient.createInitialAuthenticatorBlock({
      authenticatorBlock: blockDto,
    });

    return toSyncDto(requireFieldNotEmpty(resp.syncUpdate));
  }
}
